import styled from "styled-components";
import { mobileOnly } from "_/style/media";
import { TextLink } from "_/components/router";
import * as JobsListingS from "_/components/jobs-listing/styled";

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 2.25rem 3.125rem;
  margin: auto;

  color: ${(p) => p.theme.text.default};

  width: 100%;
  height: 100%;
`;

export const NoAttemptsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 1rem;
  width: 100%;
  color: ${(p) => p.theme.text.secondary};
  font-size: 0.8125rem;
`;

export const NoAttemptsIcon = styled.span`
  color: ${(p) => p.theme.text.muted};
  font-size: 1.1rem;
  flex-shrink: 0;
`;

export const OneRevision = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  padding: 1.25rem 1.5rem 1.5rem;
  background-color: ${(p) => p.theme.background.secondary};
  border: 1px solid ${(p) => p.theme.border.default};
  border-radius: 8px;
`;

export const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 2.5rem;
`;

export const RevisionInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 0.5rem;
`;

export const RevisionHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const RevisionLink = styled(TextLink).attrs({
  variant: "default",
})<{ variant?: never }>`
  font-size: 1.25rem;
  font-weight: 600;
`;

// Renders a row of meta fields (created by, materials, ...) separated by a
// "·" divider, inserted between every pair of adjacent fields.
export const RevisionMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: ${(p) => p.theme.text.muted};

  & > * + *::before {
    content: "·";
    margin-right: 0.5rem;
    color: ${(p) => p.theme.border.contrast};
  }

  ${mobileOnly`
    gap: 0.25rem;
  `}
`;

export const MaterialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(10rem, 16rem));
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
`;

export const MaterialCard = styled.div`
  background-color: ${(p) => p.theme.background.tertiary};
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 0.85rem;
`;

export const MaterialCardLabel = styled.div`
  font-size: 0.75rem;
  color: ${(p) => p.theme.text.muted};
  margin-bottom: 0.375rem;
`;

export const FilesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

export const FilesList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 0.85rem;
  min-width: 0;
  padding: 0.5rem 0;

  &:not(:last-child) {
    border-bottom: 0.5px solid ${(p) => p.theme.border.default};
  }

  /* Lets the filename (rendered inside the download link) shrink and wrap
     once the row runs out of space, rather than guessing a width from the
     viewport. It's not given flex-grow, so it stays snug against the
     download icon and size instead of stretching to fill the row. */
  & > a {
    min-width: 0;
  }
`;

export const FileIcon = styled.span`
  display: flex;
  flex-shrink: 0;
  color: ${(p) => p.theme.text.muted};
  font-size: 1.1rem;

  ${mobileOnly`
    display: none;
  `}
`;

// Small kind label shown alongside the icon, since STL and 3MF currently
// share the same icon (both are "a 3D model file") and would otherwise be
// indistinguishable at a glance without reading the filename.
export const FileKindTag = styled.span`
  flex-shrink: 0;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${(p) => p.theme.text.muted};
`;

// Wraps the filename onto multiple lines once its containing row runs out
// of space, rather than letting a long name dictate the row's width.
// `white-space` is reset here since the download link wrapping it forces
// nowrap for its other (single-line) usages.
export const FileName = styled.span`
  min-width: 0;
  white-space: normal;
  overflow-wrap: break-word;
  font-weight: 500;
  color: inherit;
`;

export const FileSize = styled.span`
  flex-shrink: 0;
  color: ${(p) => p.theme.text.muted};
  font-size: 0.75rem;

  ${mobileOnly`
    display: none;
  `}
`;

export const JobNumber = styled(JobsListingS.RevisionOrJobNumber)`
  font-size: inherit;
  margin-left: 0;
  text-align: center;
`;
